import { useState, useEffect, useRef } from "react";
import "./App.css";
import ChatWindow from "./ChatWindow";

// Demo agent data - will be replaced with API call to backend
const DEMO_AGENTS = [
  {
    id: "koch-org-hierarchy",
    name: "Koch Org Hierarchy",
    description: "Provides information about Koch's organizational structure and reporting relationships",
    icon: "🏛️",
    authMethods: ["Azure Token", "Microsoft Login"],
    category: "Standard",
    status: "Active",
    detailsExpanded: false,
  },
  {
    id: "org-hierarchy-analysis",
    name: "Org Hierarchy Analysis Agent",
    description: "Helps Business HRs and Application Security Analysts analyze organizational unit hierarchy, relationships, and lineage based on a provided dataset",
    icon: "📊",
    authMethods: ["No Authentication"],
    category: "Standard",
    status: "Active",
    detailsExpanded: false,
  },
  {
    id: "auto-route",
    name: "Auto Route",
    description: "Automatically discover and route to the best available agent for your task",
    icon: "🤖",
    authMethods: ["Azure Token", "Microsoft Login"],
    category: "Standard",
    status: "Active",
    detailsExpanded: false,
  },
  {
    id: "continuous-improvement",
    name: "Continuous Improvement",
    description: "The CI Agent is designed to serve as a single point of access for information related to continuous improvement initiatives",
    icon: "📊",
    authMethods: ["Azure Token", "Microsoft Login"],
    category: "Standard",
    status: "Active",
    detailsExpanded: false,
  },
  {
    id: "esc-assistant",
    name: "ESC Assistant",
    description: "ESC assistant is made to help answering employee queries we receive through chat, call, or email",
    icon: "💬",
    authMethods: ["Azure Token", "Microsoft Login"],
    category: "Standard",
    status: "Active",
    detailsExpanded: false,
  },
  {
    id: "koch-benefits",
    name: "Koch Benefits Chat Bot",
    description: "The Koch Benefits Chat Bot helps you navigate and understand your employee benefits at Koch",
    icon: "💼",
    authMethods: ["Azure Token", "Microsoft Login"],
    category: "Standard",
    status: "Active",
    detailsExpanded: false,
  },
  {
    id: "koch-knowledge",
    name: "Koch Knowledge",
    description: "Provide AI-generated answers grounded in your access to Microsoft Teams, Outlook, OneNote, and SharePoint",
    icon: "📚",
    authMethods: ["Azure Token", "Microsoft Login"],
    category: "Standard",
    status: "Active",
    detailsExpanded: false,
  },
  {
    id: "myhr-support",
    name: "MyHR Support Agent",
    description: "This agent is designed to provide functional support for users of myHR by leveraging documentation and best practices",
    icon: "🏢",
    authMethods: ["Azure Token", "Microsoft Login"],
    category: "Standard",
    status: "Active",
    detailsExpanded: false,
  },
  {
    id: "safe-travel",
    name: "Safe Travel",
    description: "Helps answer questions about traveling safe both domestically and internationally using the US State Department guidelines",
    icon: "✈️",
    authMethods: ["Azure Token", "Microsoft Login"],
    category: "Standard",
    status: "Active",
    detailsExpanded: false,
  },
];

export default function AgentCatalog() {
  const [searchQuery, setSearchQuery] = useState("");
  const [agents, setAgents] = useState(DEMO_AGENTS);
  const [selectedChatAgent, setSelectedChatAgent] = useState(null);

  // Filter agents based on search
  const filteredAgents = agents.filter((agent) => {
    const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         agent.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleChatClick = (agent) => {
    // Open the chat modal for agents with bot configuration
    setSelectedChatAgent(agent);
  };

  const closeChatWindow = () => {
    setSelectedChatAgent(null);
  };

  return (
    <div className="agent-catalog-root">
      {/* Header */}
      <div className="agent-catalog-header">
        <div className="agent-catalog-header-inner">
          <div className="logo-mark">AI</div>
          <div>
            <div className="agent-catalog-title">AI Agents</div>
            <div className="agent-catalog-subtitle">Copilot Studio agent catalog</div>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="agent-catalog-search-section">
        <div className="agent-catalog-search-bar">
          <svg
            className="agent-catalog-search-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="agent-catalog-search-input"
            placeholder="Search by agent name or capability..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        {/* Stats */}
        <div className="agent-catalog-stats">
          <span className="agent-catalog-stat-item">
            <span className="agent-catalog-stat-value">{agents.length}</span> agents
          </span>
          <span className="agent-catalog-stat-divider">·</span>
          <span className="agent-catalog-stat-item">
            <span className="agent-catalog-stat-value">{agents.filter(a => a.status === "Active").length}</span> active
          </span>
        </div>
      </div>

      {/* Agent List */}
      <div className="agent-catalog-body">
        {filteredAgents.map((agent) => (
          <div key={agent.id} className="agent-catalog-item">
            <div className="agent-catalog-item-left">
              <div className="agent-catalog-item-icon-wrapper">
                <div className="agent-catalog-item-icon">{agent.icon}</div>
                <div className="agent-catalog-item-status-indicator" title={agent.status} />
              </div>
              
              <div className="agent-catalog-item-info">
                <h3 className="agent-catalog-item-title">{agent.name}</h3>
                <p className="agent-catalog-item-description">{agent.description}</p>
                
                <div className="agent-catalog-item-meta">
                  {agent.authMethods.map((method, idx) => (
                    <span key={idx} className="agent-catalog-item-badge">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      {method}
                    </span>
                  ))}
                  <span className="agent-catalog-item-category">{agent.category}</span>
                </div>
              </div>
            </div>

            <div className="agent-catalog-item-right">
              <button
                className="agent-catalog-item-chat-btn"
                onClick={() => handleChatClick(agent)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Start Chat
              </button>
            </div>
          </div>
        ))}

      {/* Empty State */}
      {filteredAgents.length === 0 && (
        <div className="agent-catalog-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <div className="agent-catalog-empty-text">
            <strong>No agents found</strong>
            <p>Try adjusting your search or browse all available agents</p>
          </div>
        </div>
      )}
      </div>

      {/* Chat Modal - Placeholder for embedded chat */}
      {selectedChatAgent && (
        <div className="modal-overlay" onClick={closeChatWindow}>
          <div className="modal-card agent-chat-modal" onClick={(e) => e.stopPropagation()}>
            <ChatWindow agent={selectedChatAgent} onClose={closeChatWindow} />
          </div>
        </div>
      )}
    </div>
  );
}
